// Real solar (and lunar) position for worlds with a real lat/lon (SOL-1; ARCH §19,
// scope widened to Earth worlds at ERT-6). PURE (ENG-1) — float ops only, no
// `three`/DOM/tables — so the sim owns the sky and the client `sky.ts` merely renders
// the `SkyState` this module produces (via `World.skyState`). Anything carrying an
// `AtlasSolar` (Atlas or Earth) gets the true sun/moon; every other world keeps the
// generic 24 000-tick cycle byte-for-byte.
//
// The sun uses the canonical NOAA solar-position algorithm (the same one behind the
// NOAA solar calculator); the moon uses a compact low-precision series (Meeus §47,
// low-accuracy terms). Both return azimuth (clockwise from geographic north) and
// elevation (above the horizon), plus a unit direction in world axes (+x east,
// +y up, +z south — matching the Atlas ENU projection where z grows south).
//
// Parity (ARCH §19): with no `AtlasSolar` (every non-Atlas world), `skyStateFor`
// reproduces the pre-M31 generic 24 000-tick cycle byte-for-byte — same sun path,
// same `daylightFactor` — so day/night on a normal world is unchanged.
import { LIGHT, SOLAR, TICK } from '../config';
import { daylightFactor, moonPhaseOf } from '../engine/light';

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/** In-world local-solar hour at dayTime 0 — Minecraft's clock starts at 06:00. */
const DAWN_HOUR = 6;

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
/** Keep asin/acos inputs in domain so extreme latitudes never NaN (E-29). */
function clampUnit(x: number): number {
  return clamp(x, -1, 1);
}
function mod360(x: number): number {
  return ((x % 360) + 360) % 360;
}
function smoothstep(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

/** A celestial body's sky position. */
export interface SkyBody {
  /** Azimuth in radians, clockwise from geographic north (0 = N, π/2 = E). */
  azimuth: number;
  /** Elevation in radians above the horizon (−π/2 below … +π/2 zenith). */
  elevation: number;
  /** Unit world-space direction: +x east, +y up, +z south. */
  dir: { x: number; y: number; z: number };
}

/** The sky the sim hands the renderer each frame (ENG-1 seam). */
export interface SkyState {
  sun: SkyBody;
  moon: SkyBody;
  /** Sky-light scale 0..1 (LIT-6), already floored to the moonlit night value. */
  daylight: number;
  /** True when driven by real solar position (an Atlas world); false = generic. */
  real: boolean;
}

/** Serializable solar config for a real-lat/lon world — Atlas or Earth (SOL-1 /
 *  ERT-6). `epochMs` is the real UTC instant captured at world create — it fixes the
 *  calendar date (declination); the time of day then advances with the in-world clock. */
export interface AtlasSolar {
  lat: number;
  lon: number;
  epochMs: number;
}

/** Fractional Julian Day for a UTC instant. */
export function julianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2_440_587.5;
}

/** World-space unit direction for an azimuth (cw from north) + elevation. */
function dirFromAzEl(azimuth: number, elevation: number): { x: number; y: number; z: number } {
  const c = Math.cos(elevation);
  // North = −z, East = +x (ENU with z south): horizontal = sin(az)·east + cos(az)·north.
  return { x: c * Math.sin(azimuth), y: Math.sin(elevation), z: -c * Math.cos(azimuth) };
}

/**
 * Sun azimuth + elevation for a UTC instant at (lat, lon) — the NOAA solar-position
 * algorithm (timezone 0; longitude enters through the true-solar-time term). Matches
 * the NOAA solar calculator to well under a degree.
 */
export function sunPosition(lat: number, lon: number, date: Date): SkyBody {
  const jd = julianDay(date);
  const T = (jd - 2_451_545) / 36_525; // Julian century since J2000.0

  const L0 = mod360(280.46646 + T * (36_000.76983 + 0.0003032 * T)); // geom mean longitude
  const M = 357.52911 + T * (35_999.05029 - 0.0001537 * T); // geom mean anomaly
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T); // eccentricity
  const Mr = M * DEG;
  const C = // equation of centre
    Math.sin(Mr) * (1.914602 - T * (0.004817 + 0.000014 * T)) +
    Math.sin(2 * Mr) * (0.019993 - 0.000101 * T) +
    Math.sin(3 * Mr) * 0.000289;
  const trueLong = L0 + C;
  const omega = 125.04 - 1934.136 * T;
  const lambda = (trueLong - 0.00569 - 0.00478 * Math.sin(omega * DEG)) * DEG; // apparent longitude
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
  const eps = (eps0 + 0.00256 * Math.cos(omega * DEG)) * DEG; // corrected obliquity
  const decl = Math.asin(clampUnit(Math.sin(eps) * Math.sin(lambda))); // declination

  // Equation of time (minutes).
  const y = Math.tan(eps / 2) ** 2;
  const L0r = L0 * DEG;
  const eqTime =
    4 * RAD * (y * Math.sin(2 * L0r) - 2 * e * Math.sin(Mr) + 4 * e * y * Math.sin(Mr) * Math.cos(2 * L0r)
      - 0.5 * y * y * Math.sin(4 * L0r) - 1.25 * e * e * Math.sin(2 * Mr));

  // True solar time (minutes), longitude folded in via +4·lon; wrap to [0,1440).
  const utMinutes = (((date.getTime() / 86_400_000) % 1) + 1) % 1 * 1440;
  const trueSolar = (((utMinutes + eqTime + 4 * lon) % 1440) + 1440) % 1440;
  const ha = (trueSolar / 4 - 180) * DEG; // hour angle: <0 morning, >0 afternoon

  const latR = lat * DEG;
  const cosZen = clampUnit(Math.sin(latR) * Math.sin(decl) + Math.cos(latR) * Math.cos(decl) * Math.cos(ha));
  const zenith = Math.acos(cosZen);
  const elevation = Math.PI / 2 - zenith;

  // Azimuth, clockwise from north (NOAA convention).
  const denom = Math.cos(latR) * Math.sin(zenith);
  let azimuth: number;
  if (Math.abs(denom) < 1e-9) {
    azimuth = ha > 0 ? 0 : Math.PI; // sun at the pole/zenith — degenerate
  } else {
    const cosAz = clampUnit((Math.sin(latR) * cosZen - Math.sin(decl)) / denom);
    const a = Math.acos(cosAz); // 0..π measured from south
    azimuth = ha > 0 ? mod360(a * RAD + 180) * DEG : mod360(540 - a * RAD) * DEG;
  }
  return { azimuth, elevation, dir: dirFromAzEl(azimuth, elevation) };
}

/**
 * Moon azimuth + elevation for a UTC instant at (lat, lon) — a compact low-precision
 * lunar ephemeris (good to ~a degree, ample for a sky quad). Not NOAA-validated like
 * the sun; the moon is decorative.
 */
export function moonPosition(lat: number, lon: number, date: Date): SkyBody {
  const d = julianDay(date) - 2_451_545.0;
  const Lm = 218.316 + 13.176396 * d; // mean longitude
  const Mm = (134.963 + 13.064993 * d) * DEG; // mean anomaly
  const Fm = (93.272 + 13.229350 * d) * DEG; // argument of latitude
  const lambda = (Lm + 6.289 * Math.sin(Mm)) * DEG; // ecliptic longitude
  const beta = 5.128 * Math.sin(Fm) * DEG; // ecliptic latitude
  const eps = 23.4397 * DEG;

  const ra = Math.atan2(
    Math.sin(lambda) * Math.cos(eps) - Math.tan(beta) * Math.sin(eps),
    Math.cos(lambda),
  );
  const dec = Math.asin(clampUnit(Math.sin(beta) * Math.cos(eps) + Math.cos(beta) * Math.sin(eps) * Math.sin(lambda)));

  const gmst = (280.16 + 360.9856235 * d) * DEG; // Greenwich mean sidereal time
  const ha = gmst + lon * DEG - ra; // local hour angle
  const latR = lat * DEG;
  const elevation = Math.asin(clampUnit(Math.sin(latR) * Math.sin(dec) + Math.cos(latR) * Math.cos(dec) * Math.cos(ha)));
  // Azimuth clockwise from north: atan2 gives from-south, +180° → from-north.
  const azS = Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(latR) - Math.tan(dec) * Math.cos(latR));
  const azimuth = (azS + Math.PI) % (2 * Math.PI);
  return { azimuth, elevation, dir: dirFromAzEl(azimuth, elevation) };
}

/**
 * Sky-light scale from the sun's elevation (LIT-6 override for Atlas worlds): full
 * daylight above `SOLAR.DAY_ELEV_DEG`, ramping to the moonlit floor by
 * `SOLAR.NIGHT_ELEV_DEG`. Clamped to [`NIGHT_FLOOR`, `DAY`] so polar day/night can
 * never exceed the LIT-6 bounds or NaN (E-29).
 */
export function daylightFromSun(elevationRad: number): number {
  const deg = elevationRad * RAD;
  const t = smoothstep((deg - SOLAR.NIGHT_ELEV_DEG) / (SOLAR.DAY_ELEV_DEG - SOLAR.NIGHT_ELEV_DEG));
  return clamp(LIGHT.NIGHT_FLOOR + (LIGHT.DAY - LIGHT.NIGHT_FLOOR) * t, LIGHT.NIGHT_FLOOR, LIGHT.DAY);
}

/**
 * Map the in-world clock to a UTC instant. The Minecraft clock is treated as local
 * solar time at the location (dayTime 6000 = solar noon), on the calendar date fixed
 * at world create; `sunPosition` folds longitude back out, so noon really is when the
 * sun is highest (plus the true ±equation-of-time offset).
 */
export function dateForDayTime(solar: AtlasSolar, dayTime: number): Date {
  const days = Math.floor(dayTime / TICK.DAY_TICKS);
  const frac = (((dayTime % TICK.DAY_TICKS) + TICK.DAY_TICKS) % TICK.DAY_TICKS) / TICK.DAY_TICKS;
  const localSolarHours = frac * 24 + DAWN_HOUR; // 0→06:00, 6000→12:00, 18000→24:00
  const utcHours = localSolarHours - solar.lon / 15; // local-solar → UTC at this longitude
  const base = new Date(solar.epochMs);
  const utcMidnight = Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + days);
  return new Date(utcMidnight + utcHours * 3_600_000);
}

export function sunAtDayTime(solar: AtlasSolar, dayTime: number): SkyBody {
  return sunPosition(solar.lat, solar.lon, dateForDayTime(solar, dayTime));
}
export function moonAtDayTime(solar: AtlasSolar, dayTime: number): SkyBody {
  return moonPosition(solar.lat, solar.lon, dateForDayTime(solar, dayTime));
}

/** The pre-M31 generic sky: the sun rides an east–west circle (noon overhead,
 *  midnight below) with the moon opposite — the exact geometry `sky.ts` used before
 *  the SkyState seam, so non-Atlas worlds render identically (parity invariant). */
function genericSky(dayTime: number): SkyState {
  const day = ((dayTime % TICK.DAY_TICKS) + TICK.DAY_TICKS) % TICK.DAY_TICKS;
  const phase = ((day - 6000) / TICK.DAY_TICKS) * Math.PI * 2;
  const sunY = Math.cos(phase);
  const sunX = Math.sin(phase);
  const az = 0, el = 0; // not physically meaningful for the generic cycle
  return {
    sun: { azimuth: az, elevation: el, dir: { x: sunX, y: sunY, z: 0.2 } },
    moon: { azimuth: az, elevation: el, dir: { x: -sunX, y: -sunY, z: -0.2 } },
    daylight: daylightFactor(dayTime, moonPhaseOf(dayTime)),
    real: false,
  };
}

/**
 * The single sky seam (`World.skyState`). With `solar` set (Atlas), the sun/moon come
 * from real positions and daylight from the sun's elevation (LIT-6 override); with
 * `solar` null (every other world) it is the byte-identical generic cycle.
 */
export function skyStateFor(dayTime: number, solar: AtlasSolar | null): SkyState {
  if (!solar) return genericSky(dayTime);
  const sun = sunAtDayTime(solar, dayTime);
  const moon = moonAtDayTime(solar, dayTime);
  return { sun, moon, daylight: daylightFromSun(sun.elevation), real: true };
}
